import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Layers, Plus, Check, Star, Trash2, Loader2, Pencil, FilePlus2 } from 'lucide-react';
import { timeAgo } from './ICPReachHeadline';
import { ICPSegmentEditor } from './ICPSegmentEditor';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase C — tiered ICP segments. Switch the whole targeting in one tap; the
// primary segment drives discovery + auto-prospect (canonical ICP).
export const ICPSegmentsBar = ({ icp, onChanged, refreshKey }) => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pools, setPools] = useState({});
  const [editSeg, setEditSeg] = useState(null);  // segment being edited (dialog)

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${API}/api/brain/icp/segments`);
      setSegments(r.data?.segments || []);
    } catch { setSegments([]); }
    finally { setLoading(false); }
  }, []);

  const loadPools = useCallback(async () => {
    try {
      const r = await api.get(`${API}/api/brain/icp/segments/pool`);
      setPools(r.data?.pools || {});
    } catch { setPools({}); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { loadPools(); }, [loadPools, icp?.updated_at, refreshKey]);

  const chipsFromIcp = () => {
    const sp = (icp && icp.apollo_search_params) || {};
    return {
      titles: icp?.titles || sp.titles || [],
      industries: icp?.industries || sp.raw_industries || [],
      locations: icp?.locations || sp.locations || [],
      seniorities: icp?.seniorities || sp.seniorities || [],
      company_sizes: icp?.company_sizes || (icp?.profile?.firmographic?.company_sizes) || [],
      keyword: sp.keyword || '',
    };
  };

  const createSegment = async () => {
    const name = window.prompt('Name this segment (e.g. "Tier 2 — Mid-market SaaS")');
    if (!name || !name.trim()) return;
    setBusy('new');
    try {
      await api.post(`${API}/api/brain/icp/segments`, { name: name.trim(), chips: chipsFromIcp() });
      toast.success(`Segment "${name.trim()}" created from your current targeting`);
      await load();
      loadPools();
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create segment');
    } finally { setBusy(null); }
  };

  // Build a BRAND-NEW audience from scratch (empty chips) and open the editor
  // so the user can define targeting unrelated to their current ICP.
  const createBlankSegment = async () => {
    const name = window.prompt('Name your new segment (e.g. "Enterprise Fintech — US")');
    if (!name || !name.trim()) return;
    setBusy('blank');
    try {
      const r = await api.post(`${API}/api/brain/icp/segments`, {
        name: name.trim(),
        chips: { titles: [], industries: [], locations: [], seniorities: [], company_sizes: [], keyword: '' },
      });
      toast.success(`Blank segment "${name.trim()}" created — add your targeting`);
      await load();
      if (onChanged) await onChanged();
      const seg = r.data?.segment;
      if (seg) setEditSeg(seg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create segment');
    } finally { setBusy(null); }
  };

  const activate = async (seg) => {
    if (seg.is_primary) return;
    setBusy(seg.id);
    try {
      await api.post(`${API}/api/brain/icp/segments/${seg.id}/activate`);
      toast.success(`"${seg.name}" is now your primary targeting`);
      await load();
      loadPools();
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not activate segment');
    } finally { setBusy(null); }
  };

  const remove = async (seg, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete segment "${seg.name}"?`)) return;
    setBusy(seg.id);
    try {
      await api.delete(`${API}/api/brain/icp/segments/${seg.id}`);
      toast.success('Segment deleted');
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete segment');
    } finally { setBusy(null); }
  };

  if (loading) return null;
  if (segments.length === 0) return null;

  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm" data-testid="icp-segments-bar">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Layers className="w-4 h-4 text-primary" /> Targeting segments
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={createBlankSegment}
            disabled={busy === 'blank'}
            data-testid="icp-segment-blank-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            {busy === 'blank' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />}
            New blank
          </button>
          <button
            onClick={createSegment}
            disabled={busy === 'new'}
            data-testid="icp-segment-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            {busy === 'new' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            New from current
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {segments.map((seg) => (
          <div
            key={seg.id}
            onClick={() => activate(seg)}
            data-testid={`icp-segment-${seg.id}`}
            className={`group relative flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors ${
              seg.is_primary
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border bg-background hover:bg-muted text-foreground'
            }`}
            title={seg.summary}
          >
            {seg.is_primary
              ? <Star className="w-3.5 h-3.5 fill-current" />
              : busy === seg.id
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Check className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60" />}
            <span className="font-medium max-w-[220px] truncate">{seg.name}</span>
            {(() => {
              const p = pools[seg.id];
              if (!p || p.pool_size == null) return null;
              return (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 font-semibold"
                  data-testid={`icp-segment-reach-${seg.id}`}
                  title={p.estimated_at ? `as of ${timeAgo(p.estimated_at)}` : ''}
                >
                  {p.pool_size.toLocaleString()}
                </span>
              );
            })()}
            {seg.is_primary && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20" data-testid="icp-segment-primary-badge">
                Primary
              </span>
            )}
            {!seg.is_primary && (
              <button
                onClick={(e) => { e.stopPropagation(); setEditSeg(seg); }}
                data-testid={`icp-segment-edit-${seg.id}`}
                className="opacity-0 group-hover:opacity-100 hover:text-primary transition-opacity"
                title="Edit targeting"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {!seg.is_primary && (
              <button
                onClick={(e) => remove(seg, e)}
                data-testid={`icp-segment-delete-${seg.id}`}
                className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">
        The <span className="font-medium text-primary">primary</span> segment drives lead discovery + daily auto-prospecting. Click another to switch.
      </p>
      <ICPSegmentEditor
        segment={editSeg}
        open={!!editSeg}
        onClose={() => setEditSeg(null)}
        onSaved={async () => { await load(); loadPools(); if (onChanged) await onChanged(); }}
      />
    </div>
  );
};

export default ICPSegmentsBar;
